import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";

function getDaysRemaining(endDate) {
  if (!endDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
}

function UrgencyBadge({ days }) {
  if (days === null) return null;
  if (days < 0) {
    return (
      <span className="text-xs font-semibold font-[var(--font-data)] px-1.5 py-0.5 rounded bg-[var(--color-error)]/12 text-[var(--color-error)]">
        Expired
      </span>
    );
  }
  if (days <= 3) {
    return (
      <span className="text-xs font-semibold font-[var(--font-data)] px-1.5 py-0.5 rounded bg-[var(--color-error)]/12 text-[var(--color-error)]">
        {days}d left
      </span>
    );
  }
  if (days <= 7) {
    return (
      <span className="text-xs font-semibold font-[var(--font-data)] px-1.5 py-0.5 rounded bg-[var(--color-warning)]/12 text-[var(--color-warning)]">
        {days}d left
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold font-[var(--font-data)] px-1.5 py-0.5 rounded bg-[var(--color-muted)]/60 text-[var(--color-muted-foreground)]">
      {days}d left
    </span>
  );
}

export default function TrialVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/super-admin/subscriptions?payment_status=trial")
      .then(({ data }) => setVendors(data || []))
      .catch((err) => {
        console.error("Error fetching trial vendors:", err);
        setError("Failed to load");
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-[var(--color-warning)]/12 flex items-center justify-center">
            <Icon name="Clock" size={13} color="var(--color-warning)" />
          </div>
          <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
            Trial Vendors
          </h3>
        </div>
        {!loading && !error && (
          <span className="text-xs font-medium font-[var(--font-data)] px-2 py-0.5 rounded-full bg-[var(--color-warning)]/10 text-[var(--color-warning)]">
            {vendors.length}
          </span>
        )}
      </div>

      {loading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 rounded-md bg-[var(--color-muted)]/30 animate-pulse" />
          ))}
        </div>
      )}

      {error && (
        <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)] text-center py-4">{error}</p>
      )}

      {!loading && !error && vendors.length === 0 && (
        <div className="flex flex-col items-center gap-1.5 py-6 text-center">
          <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No vendors on trial</p>
        </div>
      )}

      {!loading && !error && vendors.length > 0 && (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-0.5">
          {vendors.map((v) => {
            const days = getDaysRemaining(v.end_date);
            const isUrgent = days !== null && days <= 3;
            return (
              <div
                key={v.id}
                className={`p-2.5 rounded-md border transition-colors ${
                  isUrgent
                    ? "border-[var(--color-error)]/30 bg-[var(--color-error)]/5"
                    : "border-[var(--color-border)] hover:bg-[var(--color-muted)]/40"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)] truncate">
                      {v.gym_name || v.vendor_name}
                    </p>
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] truncate mt-0.5">
                      {v.plan_name} · {v.vendor_email}
                    </p>
                  </div>
                  <UrgencyBadge days={days} />
                </div>
                {v.end_date && (
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-data)] mt-1">
                    Expires {new Date(v.end_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
